import { useEffect, useState } from 'react';

import { ApiError, apiGet } from '../../lib/api';
import {
  Alert,
  Badge,
  Card,
  CellNote,
  DataTable,
  EmptyState,
  LoadingPage,
  Num,
  PageHeader,
  Stat,
  StatGrid,
  StatusPill,
  When,
  type Column,
} from '../../ui';

interface UserRow {
  id: string;
  email: string;
  role: string;
  status: string;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  creatorId: string | null;
  creatorStatus: string | null;
  displayName: string | null;
}

/**
 * Every account, and whether it can actually be used.
 *
 * The two columns that matter are "email confirmed" and "last sign-in": an
 * unconfirmed address is an account that cannot sign in at all, and an account
 * that has never signed in is an invitation that did not land.
 */
export function AdminUsers(): React.JSX.Element {
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const body = await apiGet<{ users: UserRow[] }>('/admin/users');
        setRows(body.users);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load users.');
      }
    })();
  }, []);

  if (error) {
    return (
      <>
        <PageHeader eyebrow="Operator" title="Users" />
        <Alert tone="critical" title="Could not load users.">
          <span>{error}</span>
        </Alert>
      </>
    );
  }

  if (rows === null) return <LoadingPage eyebrow="Operator" title="Users" stats />;

  const unconfirmed = rows.filter((r) => !r.emailVerifiedAt).length;
  const neverSignedIn = rows.filter((r) => !r.lastLoginAt).length;

  const columns: Array<Column<UserRow>> = [
    {
      key: 'who',
      header: 'Account',
      primary: true,
      cell: (row) => (
        <>
          {row.displayName ?? row.email}
          {row.displayName && <CellNote>{row.email}</CellNote>}
        </>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      cell: (row) => (
        <Badge tone={row.role === 'OPERATOR' ? 'waiting' : 'neutral'} plain>
          {row.role === 'OPERATOR' ? 'Operator' : 'Creator'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Account',
      cell: (row) => <StatusPill status={row.status} domain="user" />,
    },
    {
      key: 'confirmed',
      header: 'Email confirmed',
      cell: (row) =>
        row.emailVerifiedAt ? (
          <When value={row.emailVerifiedAt} />
        ) : (
          <Badge tone="critical">not confirmed</Badge>
        ),
    },
    {
      key: 'creator',
      header: 'Creator status',
      cell: (row) =>
        row.creatorStatus ? (
          <StatusPill status={row.creatorStatus} domain="creator" />
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: 'lastLogin',
      header: 'Last sign-in',
      cell: (row) =>
        row.lastLoginAt ? (
          <When value={row.lastLoginAt} withTime />
        ) : (
          <span className="muted">never</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Users"
        lead="Who has an account, whether the address was confirmed, and whether they have ever got in."
      />

      <StatGrid>
        <Stat label="Accounts" value={<Num value={rows.length} />} />
        <Stat
          label="Unconfirmed"
          value={<Num value={unconfirmed} />}
          note={unconfirmed > 0 ? 'They cannot sign in until they confirm.' : 'Everybody confirmed.'}
          emphasis={unconfirmed > 0}
        />
        <Stat
          label="Never signed in"
          value={<Num value={neverSignedIn} />}
          note="An invitation that did not land looks like this."
        />
      </StatGrid>

      <Card title="Every account">
        <DataTable
          columns={columns}
          rows={rows}
          getKey={(row) => row.id}
          empty={
            <EmptyState title="Nobody has an account yet.">
              Issue an invitation and the first signup appears here.
            </EmptyState>
          }
        />
      </Card>
    </>
  );
}
